import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import * as academicYearsApi from "../api/academicYears.api";

import { QueryKeys } from "@/config/QueryKeys";
import { useAppSelector } from "@/redux/hooks";

function getErrorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function useAcademicYears() {
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const tenantId = useAppSelector((state) => state.auth.user?.tenantId) ?? null;

  return useQuery({
    // Tenant-scoped: this used to be a single static key shared by every tenant, so
    // switching the active tenant (e.g. a Platform Support Session switch, which changes
    // tenant via SPA navigation with no page reload) kept serving whatever was cached for
    // the *previous* tenant — stale data at best, and the wrong tenant's academic-year id
    // fed into dependent queries (sections, classes) at worst. Scoping the key means a
    // tenant change is a genuine cache miss, not a staleTime check against old data.
    queryKey: QueryKeys.academicYears.list(tenantId),
    queryFn: () => academicYearsApi.fetchAcademicYears(),
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    enabled: isAuthenticated && Boolean(tenantId),
    staleTime: 10 * 60 * 1000,
  });
}

export function useAcademicYearMutations() {
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: (input: academicYearsApi.CreateAcademicYearRequest) =>
      academicYearsApi.createAcademicYear(input),
    onSuccess: () => {
      toast.success("Academic year created");
      queryClient.invalidateQueries({ queryKey: QueryKeys.academicYears.lists() });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to create academic year"));
    },
  });

  const updateMutation = useMutation({
    mutationFn: (input: {
      id: string;
      data: academicYearsApi.UpdateAcademicYearRequest;
    }) => academicYearsApi.updateAcademicYear(input.id, input.data),
    onSuccess: (_result, variables) => {
      toast.success("Academic year updated");
      queryClient.invalidateQueries({ queryKey: QueryKeys.academicYears.lists() });
      queryClient.invalidateQueries({
        queryKey: QueryKeys.academicYears.detail(variables.id),
      });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to update academic year"));
    },
  });

  const activateMutation = useMutation({
    mutationFn: (id: string) => academicYearsApi.activateAcademicYear(id),
    onSuccess: (_result, id) => {
      toast.success("Academic year activated");
      queryClient.invalidateQueries({ queryKey: QueryKeys.academicYears.lists() });
      queryClient.invalidateQueries({ queryKey: QueryKeys.academicYears.detail(id) });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to activate academic year"));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => academicYearsApi.deleteAcademicYear(id),
    onSuccess: (_result, id) => {
      toast.success("Academic year deleted");
      queryClient.invalidateQueries({ queryKey: QueryKeys.academicYears.lists() });
      queryClient.invalidateQueries({ queryKey: QueryKeys.academicYears.detail(id) });
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error, "Failed to delete academic year"));
    },
  });

  return {
    createAcademicYear: createMutation,
    updateAcademicYear: updateMutation,
    activateAcademicYear: activateMutation,
    deleteAcademicYear: deleteMutation,
  };
}
